import { ChangeDetectorRef, Component, Inject, Injector } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { IDialogData } from "../../viewer-components/interfaces/i-dialog-data";
import { FileReaderService } from "../../viewer-components/services/readers/file-reader.service";
import { GeoService, NormalizationResult } from "../../viewer-components/services/internal-services/geo.service";
import { PendingLayer } from "../model/i-pending-layers";
import { FormValidatorService } from "../../shared/services/form-validator.service";
import { PhoenixUploadLayerDialogComponent } from "../upload-layer-dialog/phoenix-upload-layer-dialog.component";
import { OverlayDialogComponent } from "../overlay-dialog/overlay-dialog.component";

@Component({
  selector: "app-add-user-data-dialog",
  templateUrl: "./add-user-data-dialog.component.html",
  styleUrls: ["./add-user-data-dialog.component.scss"],
  standalone: false,
})
export class AddUserDataDialogComponent {
  /** Import types accessible by key (single source of truth) */
  readonly dataTypesMap: Record<string, { key: string; description: string; isText: boolean }> = {
    WMS: { key: "WMS", description: "Web Map Service (WMS)", isText: false },
    WMTS: { key: "WMTS", description: "Web Map Tile Service (WMTS)", isText: false },
    WFS: { key: "WFS", description: "Web Feature Service (WFS)", isText: false },
    ARCGIS: { key: "ARCGIS", description: "ArcGIS REST Feature Service", isText: false },
    VectorTile: { key: "VectorTile", description: "Vector Tile", isText: false },
    XYZ: { key: "XYZ", description: "XYZ Layer", isText: false },
    WKT: { key: "WKT", description: "WKT", isText: true },
    GeoJSON: { key: "GeoJSON", description: "GeoJSON", isText: true },
  };

  /** List of layers pending validation/addition */
  pendingLayers: PendingLayer[] = [];

  /** True if drag-over is active for dropzone */
  isDragOver = false;

  /** Form for URL/text input */
  form = new FormGroup({
    url: new FormControl({ value: "", disabled: false }),
    text: new FormControl({ value: "", disabled: true }),
  });

  /** Type of data being imported (WMS, WKT, GeoJSON, etc.) */
  urlType: string = this.dataTypesMap.WMS.key;

  /**
   * Returns the import types as an array for template iteration.
   */
  get dataTypesArray(): { key: string; description: string; isText: boolean }[] {
    return Object.values(this.dataTypesMap);
  }

  /**
   * Whether current type expects text input (WKT/GeoJSON) instead of URL.
   */
  get isTextType(): boolean {
    return !!this.dataTypesMap[this.urlType]?.isText;
  }

  /**
   * Creates the component and initializes the reactive form.
   * Validators are applied based on the selected import type (URL vs text).
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDialogData,
    private matDialog: MatDialog,
    private fileReaderService: FileReaderService,
    private cdr: ChangeDetectorRef,
    public injector: Injector,
  ) {
    this.applyTypeValidators();
  }

  /**
   * Handles drag-over event for dropzone.
   * @param event DragEvent
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /**
   * Handles drag-leave event for dropzone.
   * @param event DragEvent
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * Handles drop event for dropzone, processes files.
   * @param event DragEvent
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  /**
   * Opens file selector and processes selected files.
   */
  selectFile(): void {
    const fileInput = document.getElementById("uploadFile") as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  /**
   * Handles file input change event from the hidden input element.
   * @param event Event
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(input.files);
    }
    // Reset input value to allow selecting the same file again
    input.value = "";
  }

  /**
   * Processes uploaded files and adds them to pendingLayers.
   * @param files FileList
   */
  handleFiles(files: FileList): void {
    Array.from(files).forEach((file) => {
      this.addPendingLayerFromFile(file);
    });
    this.cdr.detectChanges();
  }

  /**
   * Reads a file, creates a pending row (spinner), and delegates processing to the shared handler.
   * @param file File
   */
  addPendingLayerFromFile(file: File): void {
    const pendingLayer: PendingLayer = {
      layerName: file.name,
      data: null,
      errorMessages: [],
      status: "pending",
      summary: { totalFeatures: 0, validFeatures: 0, invalidFeatures: 0 },
    };
    this.pendingLayers.push(pendingLayer);
    const layerIndex = this.pendingLayers.length - 1;

    this.fileReaderService.readFile(file).subscribe({
      next: (normalization) => {
        this.processAndAddLayer(normalization, file.name, layerIndex);
      },
      error: (err) => {
        this.handleProcessError(layerIndex, err);
      },
    });
  }

  /**
   * Removes a pending layer by index emitted from the row component.
   * Keeps the list reactive and triggers change detection.
   * @param index Index of the layer to remove.
   */
  removePending(index: number): void {
    if (index >= 0 && index < this.pendingLayers.length) {
      this.pendingLayers.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  showRemoveLayerDialog(index: number) {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove this layer?",
        isConfirmation: true,
      },
      injector: this.injector,
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.removePending(index);
      }
    });
  }

  /**
   * Returns true if there is at least one valid feature in any pending layer.
   * Used to enable the Add Valid Layers button.
   */
  get hasAnyValidFeature(): boolean {
    return this.pendingLayers.some(
      (layer) => layer.data && Array.isArray(layer.data.features) && layer.data.features.length > 0,
    );
  }

  /**
   * Returns true if all pending layers are processed (not pending/loading).
   */
  get allPendingProcessed(): boolean {
    return this.pendingLayers.length > 0 && this.pendingLayers.every((l) => l.status !== "pending");
  }

  /**
   * Finalizes and adds all valid features from pending layers to the map, then closes the dialog.
   * Layers with at least one valid feature are added, invalid features are excluded.
   */
  addValidLayers(): void {
    // Collect all valid layers for batch add
    const validLayers = this.pendingLayers
      .filter(
        (layer) =>
          layer.status === "valid" &&
          layer.data &&
          Array.isArray(layer.data.features) &&
          layer.data.features.length > 0,
      )
      .map((layer) => ({
        collection: {
          ...layer.data,
          properties: {
            ...(layer.data.properties || {}),
            name: layer.layerName,
          },
        },
      }));
    if (validLayers.length > 0) {
      this.data.viewerService.addFeatureCollections(validLayers);
    }
    this.pendingLayers = [];
    this.matDialog.closeAll();
    this.cdr.detectChanges();
  }

  /**
   * Summarize a NormalizationResult object into counters and aggregated errors.
   */
  private summarizePreparedData(normalization: NormalizationResult): {
    total: number;
    valid: number;
    invalid: number;
    errors: string[];
  } {
    const data = normalization?.data;
    const errorsArr = Array.isArray(normalization?.errors) ? normalization.errors : [];
    const valid = Array.isArray(data?.features) ? data.features.length : 0;
    const invalid = errorsArr.length;
    const total = valid + invalid;
    const errors = errorsArr.map((e: any) => e.message).filter(Boolean);
    return { total, valid, invalid, errors };
  }

  /**
   * Shared flow to populate/update a pending layer from a NormalizationResult.
   * - If updateIndex is provided: updates an existing 'pending' row (file flow).
   * - Otherwise: pushes a new finalized row (text flow).
   */
  private processAndAddLayer(normalization: NormalizationResult, layerName: string, updateIndex?: number): void {
    try {
      const summary = this.summarizePreparedData(normalization);
      // Only valid if there is at least one valid feature
      const layerStatus: "valid" | "error" = summary.valid > 0 ? "valid" : "error";

      if (typeof updateIndex === "number") {
        Object.assign(this.pendingLayers[updateIndex], {
          data: normalization.data,
          errorMessages: summary.errors,
          status: layerStatus,
          summary: {
            totalFeatures: summary.total,
            validFeatures: summary.valid,
            invalidFeatures: summary.invalid,
          },
        });
      } else {
        const layer: PendingLayer = {
          layerName,
          data: normalization.data,
          errorMessages: summary.errors,
          status: layerStatus,
          summary: {
            totalFeatures: summary.total,
            validFeatures: summary.valid,
            invalidFeatures: summary.invalid,
          },
        };
        this.pendingLayers.push(layer);
      }
      this.cdr.detectChanges();
    } catch (error: any) {
      const message = typeof error === "string" ? error : error?.message || "Unknown error";
      if (typeof updateIndex === "number") {
        this.handleProcessError(updateIndex, message);
      } else {
        const layer: PendingLayer = {
          layerName,
          data: null,
          errorMessages: [message],
          status: "error",
          summary: { totalFeatures: 0, validFeatures: 0, invalidFeatures: 0 },
        };
        this.pendingLayers.push(layer);
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Shared error handler to update an existing pending row with error state and empty counters.
   */
  private handleProcessError(index: number, error: any): void {
    const message = typeof error === "string" ? error : error?.message || "Unknown error";
    Object.assign(this.pendingLayers[index], {
      data: null,
      errorMessages: [message],
      status: "error",
      summary: { totalFeatures: 0, validFeatures: 0, invalidFeatures: 0 },
    });
    this.cdr.detectChanges();
  }

  /**
   * Cancels or resets the dialog depending on pending layers state.
   */
  cancelOrReset(): void {
    if (this.pendingLayers.length === 0) {
      this.matDialog.closeAll();
    } else {
      this.pendingLayers = [];
      this.form.reset();
      this.urlType = "WMS";
      this.isDragOver = false;
      this.applyTypeValidators();
      this.cdr.detectChanges();
    }
  }

  /**
   * Enables/disables controls and applies validators based on the selected type.
   * Validators are reassigned only when the type changes (select).
   */
  private applyTypeValidators(): void {
    const urlCtrl = this.form.get("url")!;
    const textCtrl = this.form.get("text")!;

    if (this.isTextType) {
      urlCtrl.clearValidators();
      urlCtrl.disable({ emitEvent: false });

      const textValidators =
        this.urlType === this.dataTypesMap.WKT.key
          ? [Validators.required, FormValidatorService.wkt]
          : [Validators.required, FormValidatorService.geojson];

      textCtrl.setValidators(textValidators);
      textCtrl.enable({ emitEvent: false });
      textCtrl.updateValueAndValidity({ emitEvent: false });
    } else {
      // URL mode: always apply required + generic URL (+ ArcGIS when applicable)
      textCtrl.clearValidators();
      textCtrl.disable({ emitEvent: false });

      const validators = [Validators.required, FormValidatorService.genericUrl];
      if (this.urlType === this.dataTypesMap.ARCGIS.key)
        validators.push(FormValidatorService.arcgisFeatureServiceUrlDetailed);
      urlCtrl.setValidators(validators);
      urlCtrl.enable({ emitEvent: false });
      urlCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Called when urlType changes to update validators and clear text if switching away from text type.
   */
  onUrlTypeChange(): void {
    this.applyTypeValidators();
    // If switching away from text type, clear text field and reset validation
    if (!this.isTextType) {
      this.form.controls.text.setValue("");
      this.form.controls.text.markAsPristine();
      this.form.controls.text.markAsUntouched();
      this.form.controls.text.updateValueAndValidity();
    }
  }

  /**
   * Generates a unique layer name for content layers (WKT/GeoJSON) based on type and current datetime.
   * If a duplicate exists, appends a random 2-digit suffix.
   * @param type string (WKT, GeoJSON, etc.)
   * @param existingNames string[] (list of current layer names)
   * @returns string unique layer name
   */
  private generateContentLayerName(type: string, existingNames: string[]): string {
    // Use ISO string, but only date and time up to minutes, replace T and : for readability
    const now = new Date();
    const iso = now.toUTCString(); // e.g. '2025-09-17T15:32:00.000Z'
    let layerName = `content_${type}_${iso}`;
    if (existingNames.includes(layerName)) {
      const randomSuffix = Math.floor(Math.random() * 90 + 10); // 10-99
      layerName += `_${randomSuffix}`;
    }
    return layerName;
  }

  /**
   * Proceeds to the next step:
   * - For text types (WKT/GeoJSON), adds to pendingLayers if valid and not already present, then proceeds.
   * - For URL types, opens the classic upload dialog when the URL is valid.
   */
  proceedToNext(): void {
    if (this.isTextType) {
      if (this.form.controls.text.valid) {
        const layerName = this.generateContentLayerName(
          this.urlType,
          this.pendingLayers.map((l) => l.layerName),
        );
        this.createPendingFromTextWithName(layerName);
      }
      return;
    }
    if (this.form.controls.url.valid) {
      const dialog = this.matDialog.open(PhoenixUploadLayerDialogComponent, {
        data: {
          viewerService: this.data.viewerService,
          url: this.form.controls.url.value,
          urlType: this.urlType,
        },
        panelClass: "medium-panel-dialog",
        autoFocus: false,
        injector: this.injector,
      });
      dialog.afterClosed().subscribe(() => {});
    }
  }

  /**
   * Adds a new pending layer from text input (WKT/GeoJSON) using the same processor as files, with custom layer name.
   */
  private createPendingFromTextWithName(layerName: string): void {
    try {
      const content = this.form.controls.text.value;
      const input =
        this.urlType === this.dataTypesMap.WKT.key
          ? GeoService.wktToFeatureCollection(content, false)
          : JSON.parse(content);
      const normalization = this.fileReaderService.prepareGeoJson(input, layerName);
      this.processAndAddLayer(normalization, layerName);
    } catch {
      // ignore invalid JSON/WKT; validation is handled by form validators
      return;
    }
  }
}
